import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  customsDeclarations,
  type CustomsDeclarationListItem,
} from '@/api/customsDeclarations';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import DocListFilters, { ContractorFilter, DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { agOrgCol, useOrgNames } from '@/shared/hooks/useOrgNames';
import { useQuery } from '@tanstack/react-query';
import { contractors as contractorsApi } from '@/api/catalogs';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CustomsDeclarationsPage() {
  const orgNames = useOrgNames();
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const contractorName = useMemo(() => {
    const m = new Map<string, string>();
    contractorsQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [contractorsQ.data]);
  const navigate = useNavigate();
  // Стандартні фільтри списків (2026-07-29, ревізія Покупок).
  const f = useDocListFilters();
  // 2026-07-31: пошуковий фільтр за постачальником (через рядки-приходи).
  const [contractorFilter, setContractorFilter] = useState('');
  // 2026-08-09 (Норма #29): серверна пагінація + сортування.
  const { datasource, total } = useInfiniteGridDatasource<CustomsDeclarationListItem>(
    (limit, offset, sort) => customsDeclarations.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      contractor_id: contractorFilter || undefined,
      posted: f.status === 'posted' ? true : f.status === 'draft' ? false : undefined,
      // Період — за ДАТОЮ ВМД (customs_date, рішення власника).
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [contractorFilter, ...f.queryKey],
    { attachmentsType: 'doc.customs_declarations' },
  );
  // Контроль підписання (0429): колонка «Підпис» + фон непідписаних.
  const sc = useSignControlInfinite<CustomsDeclarationListItem>('doc.customs_declarations', f.orgId);

  const cols = useMemo<ColDef<CustomsDeclarationListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: CustomsDeclarationListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№ внутр.', width: 130, sortable: true },
    { field: 'customs_number', headerName: '№ ВМД', flex: 1, minWidth: 180, sortable: true },
    {
      field: 'customs_date', headerName: 'Дата ВМД', width: 130, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    agOrgCol(orgNames),
    {
      field: 'supplier_contractor_id', headerName: 'Постачальник',
      flex: 1, minWidth: 200, sortable: true,
      valueFormatter: (p) => (p.value ? contractorName.get(p.value) ?? '' : ''),
    },
    {
      field: 'total_customs_value', headerName: 'Митна варт.', width: 140, sortable: false,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      field: 'total_cost_addition', headerName: 'У собівартість', width: 150, sortable: false,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      field: 'total_vat', headerName: 'ПДВ імпорт', width: 130, sortable: false,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 120, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], []);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Митні декларації (ВМД при імпорті)<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/customs-declarations/new')}>
          + Новий документ
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[
          { value: '', label: 'Усі статуси' },
          { value: 'posted', label: 'Проведені' },
          { value: 'draft', label: 'Чернетки' },
        ]} />
        <ContractorFilter value={contractorFilter} onChange={setContractorFilter}
          label="постачальник" />
      </DocListFiltersRow>
      <InfiniteDocGrid<CustomsDeclarationListItem>
        copyRow={(r) => navigate(`/customs-declarations/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => customsDeclarations.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="customs-declarations"
        onRowDoubleClicked={(r) => navigate(`/customs-declarations/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
